import { PUBLISHER_EVENTS, PUBLISHER_STATUSES } from './enums';
import { setGetUserMediaCalled } from './lock';
import { PeerConnection, SessionDescription, IceCandidate } from './adapters';
import WebRTCBase from './base';

export default class Publisher extends WebRTCBase {
  /**
   * Creates new instance of publisher with passed url and options
   * @param {string} streamUrl
   * @param {object} opts
   * @param {object} opts.preview
   * @param {object} opts.previewOptions - it's keys'll be applied to preview dom node as attributes
   * @param {boolean} opts.previewOptions.autoplay
   * @param {boolean} opts.previewOptions.controls
   * @param {boolean} opts.previewOptions.muted
   * @param {object} opts.constraints - video constraints
   * @param {boolean} opts.constraints.audio
   * @param {boolean} opts.constraints.video
   * @param {function} opts.onWebsocketClose
   * @param {boolean} shouldLog
   * @param {boolean} opts.trackStats
   * @param {object} opts.statsContainer
   * @param {object} opts.videoPreview
   * @param {string} opts.password
   */
  constructor(streamUrl, opts = {}, shouldLog = false) {
    super(streamUrl, opts, shouldLog);

    const self = this;
    this.status = PUBLISHER_STATUSES.INITIALIZING;
    if (opts.preview) {
      this.videoContainer = opts.preview;
      if (opts.previewOptions) {
        this.videoContainer.autoplay = opts.previewOptions.autoplay;
        this.videoContainer.controls = opts.previewOptions.controls;
        this.videoContainer.muted = opts.previewOptions.muted;
      }
      this.videoContainer.addEventListener('loadedmetadata', function() {
        self.log(
          `Local video videoWidth: ${this.videoWidth}px,  videoHeight: ${
            this.videoHeight
          }px`,
        );
      });
    }

    this.trackStats = opts.trackStats;
    this.statsContainer = opts.statsContainer;

    this.password = opts.password;

    this.videoPreview = opts.videoPreview;

    this.constraints = opts.constraints;
    this.onWebsocketClose = opts.onWebsocketClose;
  }

  /**
   * @param {object} opts
   * @param {object} opts.openPeerConnectionOptions
   * @param {object} opts.openPeerConnectionOptions.getMediaOptions
   * @param {function} opts.openPeerConnectionOptions.getMediaOptions.onGetUserMediaError
   */

  start = opts => {
    let url = new URL(this.url);
    url.protocol = 'wss';
    url.pathname = url.pathname + '/webrtc/publish';
    if (this.password) {
      var search_params = url.searchParams;
      search_params.set('password', this.password);
      url.search = search_params.toString();
    }

    this.ws = new WebSocket(url.toString());
    this.opts = opts;

    this.ws.onmessage = this.onWsMsg;
    this.ws.onclose = event => {
      this.log("Publisher::WS 'onClose' fired.", event);
      this.isFirstMsgReceived = false;
      if (typeof this.onWebsocketClose === 'function') {
        this.onWebsocketClose(event);
      }
    };

    this.ws.onerror = event => {
      this.log("Publisher::WS 'onError' fired, no handler presented", event);
      this.isFirstMsgReceived = false;
    };

    if (!this.isSafariOrIOS) {
      this.initSilentAudio();
    }
  };

  stop = () => {
    this.isFirstMsgReceived = false;
    this.clearLocalStreamSource();

    if (this.peerConnection) {
      this.peerConnection.close();
      this.peerConnection = null;
    }

    if (this.ws) {
      this.ws.close();
      this.ws = null;
    }

    this.status = PUBLISHER_STATUSES.STOPPED;
  };

  /**
   * @param {object} opts
   * @param {object} opts.getMediaOptions
   * @param {function} opts.getMediaOptions.onGetUserMediaError
   * @param {object} config
   */
  openPeerConnection = (opts, config = null) => {
    if (this.isSafariOrIOS) {
      this.getMedia(opts ? opts.getMediaOptions : null, cb => {
        this.log('Publisher::openPeerConnection::cb');
        this.peerConnection = new PeerConnection(config);
        this.peerConnection.stream_id = 'local1';
        this.peerConnection.onicecandidate = this.gotIceCandidate;
        this.log(
          'Publisher::openPeerConnection::peerConnection',
          this.peerConnection,
        );
        if (typeof cb === 'function') {
          cb();
        }
      });
    } else {
      this.peerConnection = new PeerConnection(config);
      this.peerConnection.stream_id = 'local1';
      this.peerConnection.onicecandidate = this.gotIceCandidate;
      // this.peerConnection.onconnectionstatechange = event => {
      //   this.log('Publisher:onconnectionstatechange:', event);
      // };
      // this.peerConnection.oniceconnectionstatechange = event => {
      //   this.log('Publisher:oniceconnectionstatechange:', event);
      // };

      if (this.trackStats) {
        window.setInterval(() => {
          this.peerConnection &&
            this.peerConnection.getStats(null).then(stats => {
              let statsOutput = '';

              stats.forEach(report => {
                statsOutput +=
                  `<h2>Report: ${report.type}</h3>\n<strong>ID:</strong> ${
                    report.id
                  }<br>\n` +
                  `<strong>Timestamp:</strong> ${report.timestamp}<br>\n`;

                // Now the statistics for this report; we intentially drop the ones we
                // sorted to the top above

                Object.keys(report).forEach(statName => {
                  if (
                    statName !== 'id' &&
                    statName !== 'timestamp' &&
                    statName !== 'type'
                  ) {
                    statsOutput += `<strong>${statName}:</strong> ${
                      report[statName]
                    }<br>\n`;
                  }
                });
              });

              if (this.statsContainer) {
                const statsBox = this.statsContainer;
                if (statsBox instanceof HTMLElement) {
                  statsBox.innerHTML = statsOutput;
                }
              }
            });
        }, 1000);
      }

      this.getMedia(opts ? opts.getMediaOptions : null);
    }
  };

  clearLocalStreamSource = () => {
    if (this.stream) {
      this.stream.getTracks().forEach(function(track) {
        track.stop();
      });
    }
  };

  onWsAnswerReceived = message => {
    const description = new SessionDescription(message);
    this.peerConnection.setRemoteDescription(description);
    this.log('Publisher::answer');
  };

  onWsIceCandidateReceived = message => {
    this.log('Publisher::candidate');
    const candidate = new IceCandidate(message.candidate);
    this.peerConnection.addIceCandidate(candidate).then(() => {
      this.status = PUBLISHER_STATUSES.STREAMING;
      this.emitter.emit(PUBLISHER_EVENTS.STREAMING);
      this.log('Publisher::streaming', this.stream);
      // this.stream.onaddtrack = function(event) {
      //   this.log('Publisher::onAddTrack', event);
      // };
      // this.stream.onremovetrack = function(event) {
      //   this.log('Publisher::onRemoveTrack', event);
      // };
      if (this.videoContainer) {
        this.videoContainer.srcObject = this.stream;
      }
    });
  };

  onWsMsg = event => {
    const message = JSON.parse(event.data);

    if (!this.isFirstMsgReceived) {
      this.log('Publisher::first message received', message);
      this.isFirstMsgReceived = true;

      const opts = this.opts ? this.opts.openPeerConnectionOptions : null;
      if (message.config.turn) {
        this.log('Publisher::turn setup');
        const { addr, pass, port, user } = message.config.turn;
        this.openPeerConnection(opts, {
          iceServers: [
            {
              urls: [`turn:${addr}:${port}?transport=tcp`],
              username: user,
              credential: pass,
            },
          ],
        });
      } else {
        this.log('Player::common setup');
        this.openPeerConnection(opts);
      }
    }

    if (message.type === 'answer') {
      this.onWsAnswerReceived(message);
    } else if (message.type === 'candidate') {
      this.onWsIceCandidateReceived(message);
    }
  };

  gotIceCandidate = event => {
    if (event.candidate) {
      this.sendWsMsg({
        type: 'candidate',
        stream_id: 'local1',
        label: event.candidate.sdpMLineIndex,
        id: event.candidate.sdpMid,
        candidate: event.candidate,
      });
    }
  };

  /**
   * @param {object} opts
   * @param {function} opts.onGetUserMediaError
   * @param {function} [cb]
   */
  getMedia = (opts, cb) => {
    //this.clearLocalStreamSource();
    navigator.mediaDevices
      .getUserMedia({ ...this.constraints, video: true })
      .then(stream => {
        setGetUserMediaCalled();
        this.log('Publisher::mediaDevices.getUserMedia success.');
        if (typeof cb === 'function') {
          cb(() => {
            this.log('Publisher::getMedia::cb');
            this.gotMedia(stream);
          });
        } else {
          this.gotMedia(stream);
        }

        if (
          this.videoPreview &&
          this.videoPreview instanceof HTMLMediaElement
        ) {
          this.videoPreview.srcObject = stream;
        }

        if (!this.isSafariOrIOS || !this.silentAudioCapture) {
          navigator.mediaDevices.ondevicechange = this.onDeviceChange;
        }
      })
      .catch(error => {
        this.log(
          'Publisher::mediaDevices.getUserMedia exception occured.',
          error,
        );
        if (opts && opts.onGetUserMediaError) {
          opts.onGetUserMediaError(error);
        }
      });
  };

  async getDevices() {
    // Получаем список устройств
    if (!navigator.mediaDevices || !navigator.mediaDevices.enumerateDevices) {
      console.log('enumerateDevices() not supported.');
      return;
    }

    // List cameras and microphones.
    let deviceList = [];

    await navigator.mediaDevices
      .enumerateDevices()
      .then(function(devices) {
        deviceList = devices;
        return deviceList;
      })
      .catch(function(err) {
        console.log(err.name + ': ' + err.message);
        return deviceList;
      });

    return deviceList;
  }

  onDeviceChange = event => {
    // Если поменялась конфигурация устройств
    this.log('Publisher::Device configuration changed', event);
    if (this.activeDeviceList) {
      const currentAudioDevices = this.activeDeviceList.filter(
        device => device.kind === 'audioinput',
      );
      const currentVideoDevices = this.activeDeviceList.filter(
        device => device.kind === 'videoinput',
      );
      let aviableAudioDevices = [];
      let aviableVideoDevices = [];

      this.getDevices.bind(this);
      this.getDevices().then(devices => {
        aviableAudioDevices = devices.filter(
          device => device.kind === 'audioinput',
        );
        aviableVideoDevices = devices.filter(
          device => device.kind === 'videoinput',
        );
        if (currentAudioDevices.length !== 0 && !aviableAudioDevices.length) {
          this.log('Publisher::Audio input devices lost');
          if (this.silentAudioCapture) {
            const silentTracks = this.silentAudioCapture.getAudioTracks();
            this.audioSender.sender.replaceTrack(silentTracks[0]);
          }
        } else if (
          !currentAudioDevices.length &&
          aviableAudioDevices.length !== 0
        ) {
          this.log('Publisher::New audio input device discovered');
          if (this.silentAudioCapture) {
            navigator.mediaDevices
              .getUserMedia({ audio: true })
              .then(stream => {
                const audioTracks = stream.getAudioTracks();
                if (audioTracks.length > 0) {
                  this.log(`Using audio device: ${audioTracks[0].label}`);
                }
                this.audioSender.sender.replaceTrack(audioTracks[0]);
              });
          }
        }
        this.activeDeviceList = devices;
      });
    }
  };

  gotMedia = stream => {
    this.stream = stream;
    this.log('Publisher::got', this.stream);
    this.log('Publisher::gotMedia::peerConnection', this.peerConnection);
    let hasAudio = false;
    let hasVideo = false;
    if (this.silentAudioElement) {
      if (this.silentAudioElement.captureStream) {
        this.silentAudioCapture = this.silentAudioElement.captureStream();
      } else if (this.silentAudioElement.mozCaptureStream) {
        this.silentAudioCapture = this.silentAudioElement.mozCaptureStream();
      } else {
        this.log(
          'Publisher::Stream capture is not supported - no silent audio stream',
        );
        this.silentAudioCapture = null;
      }
    }

    if ('video' in this.constraints) {
      hasVideo = this.constraints.video;
    }

    this.stream.getTracks().forEach(track => {
      if (track.kind === 'audio') {
        hasAudio = true;
        if (this.isSafariOrIOS) {
          this.audioSender = this.peerConnection.addTrack(track, this.stream);
        } else {
          this.audioSender = this.peerConnection.addTransceiver(
            track,
            this.stream,
          );
        }
        return;
      }
      if (!hasVideo && track.kind === 'video') {
        track.enabled = false;
      }
      if (this.isSafariOrIOS) {
        this.peerConnection.addTrack(track, this.stream);
      } else {
        this.peerConnection.addTransceiver(track, this.stream);
      }
    });

    if (!hasAudio) {
      // Add disabled audio track
      if (this.silentAudioCapture) {
        this.silentAudioCapture.getTracks().forEach(track => {
          this.audioSender = this.peerConnection.addTransceiver(
            track,
            this.silentAudioCapture,
          );
        });
        hasAudio = true;
      }
    }

    const offerOptions = {
      offerToReceiveVideo: true,
      offerToReceiveAudio: hasAudio,
      // voiceActivityDetection: false,
    };

    this.peerConnection
      .createOffer(offerOptions)
      .then(description => {
        return this.peerConnection.setLocalDescription(description);
      })
      .then(() => {
        this.sendWsMsg(this.peerConnection.localDescription);
      })
      .catch(error => {
        const errorMessage = 'Error establishing P2P connection.';
        this.log(errorMessage, error);
      });
  };

  initSilentAudio() {
    let silentAudio = this.createSilentAudio(10, 44100);

    this.silentAudioElement = document.createElement('audio');
    this.silentAudioElement.id = 'audio-player';
    this.silentAudioElement.src = silentAudio;
    this.silentAudioElement.type = 'audio/wav';
    this.silentAudioElement.loop = true;
    document.body.appendChild(this.silentAudioElement);
    this.silentAudioElement.play();
  }

  mute() {
    this.stream.getTracks().forEach(track => {
      if (track.kind === 'audio') {
        track.enabled = !track.enabled;
      }
    });
  }

  createSilentAudio = function(time, freq = 44100) {
    const length = time * freq;
    const AudioContext =
      window.AudioContext ||
      window.webkitAudioContext ||
      window.mozAudioContext;
    if (!AudioContext) {
      console.log('No Audio Context');
    }
    const context = new AudioContext();
    const audioFile = context.createBuffer(1, length, freq);

    function bufferToWave(abuffer, len) {
      let numOfChan = abuffer.numberOfChannels,
        length = len * numOfChan * 2 + 44,
        buffer = new ArrayBuffer(length),
        view = new DataView(buffer),
        channels = [],
        i,
        sample,
        offset = 0,
        pos = 0;

      // write WAVE header
      setUint32(0x46464952);
      setUint32(length - 8);
      setUint32(0x45564157);

      setUint32(0x20746d66);
      setUint32(16);
      setUint16(1);
      setUint16(numOfChan);
      setUint32(abuffer.sampleRate);
      setUint32(abuffer.sampleRate * 2 * numOfChan);
      setUint16(numOfChan * 2);
      setUint16(16);

      setUint32(0x61746164);
      setUint32(length - pos - 4);

      // write interleaved data
      for (i = 0; i < abuffer.numberOfChannels; i++)
        channels.push(abuffer.getChannelData(i));

      while (pos < length) {
        for (i = 0; i < numOfChan; i++) {
          // interleave channels
          sample = Math.max(-1, Math.min(1, channels[i][offset])); // clamp
          sample = (0.5 + sample < 0 ? sample * 32768 : sample * 32767) | 0; // scale to 16-bit signed int
          view.setInt16(pos, sample, true); // write 16-bit sample
          pos += 2;
        }
        offset++; // next source sample
      }

      // create Blob
      return new Blob([buffer], { type: 'audio/wav' });

      function setUint16(data) {
        view.setUint16(pos, data, true);
        pos += 2;
      }

      function setUint32(data) {
        view.setUint32(pos, data, true);
        pos += 4;
      }
    }

    return URL.createObjectURL(bufferToWave(audioFile, length));
  };

  /**
   *
   * @param {array} args
   * @param {string} args[0] - event name
   * @param {function} args[1] - callback for event
   * @returns {*}
   */
  on = (...args) => {
    return this.emitter.on.apply(this.emitter, args);
  };
}
